/**
 * Screen-scoped design tokens for **Settings → Enterprise SSO**.
 *
 * This screen predates the Settings re-skin and had drifted onto its own ad hoc
 * palette. These tokens instead reuse the exact values already established by
 * the re-skinned screens (`SecurityPolicies.tokens.ts`, `Settings.tokens.ts`)
 * so Enterprise SSO reads as the same product, not a bolt-on. Stays screen-local
 * until the shared values get promoted into the MUI theme.
 */

import { button, contentCard, formField, notification, signer, white } from "theme/colors";

export const enterpriseSsoTokens = {
  /** White rounded card chrome — identical chrome to every other Settings card. */
  card: {
    border: contentCard.borderMuted,
    borderWidth: "1px",
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
  },
  header: {
    gap: "12px",
  },
  /**
   * Protocol note under the page title. No sibling Settings screen carries a
   * subtitle, so this borrows the description colour and size the settings
   * cards already use for secondary copy rather than inventing a new voice.
   */
  pageSubtitle: {
    color: signer.text.meta,
    fontSize: "12.5px",
    fontWeight: 500,
    lineHeight: "normal",
    marginTop: "4px",
  },
  /**
   * Filled brand action ("Add Connector"). Chrome and size both come straight
   * from the global `button` token — 40px / 20px / 13px — so it matches the
   * primary action on Roles and Teams, which it now sits in line with.
   */
  primaryButton: button,
  table: {
    gutter: "24px",
    columns: "1.7fr 100px 1.7fr 1fr 1.3fr 120px 40px",
    minWidth: 880,
    headerPaddingY: "10px",
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    headerFontSize: "10.5px",
    headerFontWeight: 700,
    headerLetterSpacing: "0.09em",
    headerColor: signer.text.faint,
    rowPaddingY: "16px",
    rowDivider: signer.border.divider,
    rowFontSize: "13px",
    rowColor: signer.ink,
    subTextColor: signer.text.faint,
    subTextFontSize: "11.5px",
    actionColor: signer.text.faint,
    emptyPaddingY: "40px",
    emptyFontSize: "12.5px",
    emptyColor: signer.text.meta,
  },
  /** Small rounded label used for domains / login-policy / status cells. */
  chip: {
    fontSize: "11px",
    fontWeight: 700,
    radius: "999px",
    paddingX: "8px",
    height: 22,
    neutralBg: signer.surface.muted,
    neutralColor: signer.ink,
    statusDraft: { bg: signer.warn.tint, color: signer.warn.main },
    statusActive: { bg: notification.success.bg, color: notification.success.icon },
    statusInactive: { bg: signer.surface.muted, color: signer.text.faint },
  },
  /** Centred empty state shown when the plan doesn't include SSO. */
  planBlocked: {
    iconSize: 56,
    iconRadius: "14px",
    iconBg: signer.surface.muted,
    iconColor: signer.ink,
    titleColor: signer.ink,
    titleFontSize: "16px",
    titleFontWeight: 800,
    descColor: signer.text.meta,
    descFontSize: "12.5px",
  },
  /** Monospace value box in the "SP Setup Info" dialog. */
  valueBox: {
    bg: formField.bg,
    border: formField.border,
    color: signer.ink,
    labelColor: "#475569",
  },

  attributeMapping: {
    header: {
      iconSize: 40,
      iconRadius: "10px",
      iconBg: signer.purple.tint,
      iconColor: signer.purple.main,
    },
    banner: {
      bg: signer.purple.tint,
      border: signer.purple.border,
      icon: signer.purple.main,
      text: signer.text.secondary,
    },
    sectionIcon: signer.text.meta,
    requiredChip: { bg: signer.surface.muted, color: signer.ink },
    row: {
      radius: "12px",
      bg: white,
      border: signer.border.light,
      borderEnabled: formField.border,
      iconRest: signer.text.faint,
      iconActive: signer.purple.main,
      iconActiveBg: "rgba(109, 82, 162, 0.1)",
    },
    /** Read-only preview of the server's built-in candidates, shown in Default mode. */
    defaultChip: { bg: signer.surface.canvas, border: signer.border.light, color: signer.text.meta },
    /** One draggable row in the ordered attribute list. */
    pathRow: {
      draggingBg: signer.purple.faint,
      draggingShadow: "0 8px 20px rgba(33, 21, 71, 0.14)",
      handle: "#B4AFC4",
      handleActive: signer.purple.main,
      index: signer.text.faint,
    },
    /** Default/Custom choice cards. */
    modeOption: {
      border: signer.border.light,
      borderSelected: signer.purple.main,
      bgSelected: "rgba(109, 82, 162, 0.06)",
    },
    error: "#B3261E",
  },
} as const;

/**
 * Phone and tablet values for **Settings → Enterprise SSO**.
 *
 * This screen has no mock in `Settings - Mobile & Tablet.dc.html`, so the
 * values follow the language the neighbouring settings screens already use at
 * these sizes: a full-width 44px primary action and one card per record in
 * place of the table. Applied below `md`; desktop is untouched.
 */
export const enterpriseSsoResponsiveTokens = {
  action: { height: "44px", paddingX: "20px", fontSize: "13px" },
  /** One connector, drawn as its own card instead of a seven-column row. */
  card: {
    padding: "14px 16px",
    gap: "10px",
    headGap: "10px",
    nameFontSize: "13.5px",
    subFontSize: "11.5px",
    chipGap: "6px",
    labelFontSize: "10px",
    labelLetterSpacing: "0.09em",
  },
} as const;

export default enterpriseSsoTokens;
